import { useEffect } from 'react';
import type { FC, PropsWithChildren } from 'react';
import { DARK_COLOR_SCHEME } from 'types/settings';

import { Helmet } from 'react-helmet';
import { NavLink, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import weekText from 'utils/weekText';
import Footer from 'views/layout/Footer';
import Navtabs from 'views/layout/Navtabs';
import Notification from 'views/components/notfications/Notification';
import ErrorBoundary from 'views/errors/ErrorBoundary';
import ErrorPage from 'views/errors/ErrorPage';
import { trackPageView } from 'bootstrapping/matomo';
import { isIOS } from 'bootstrapping/browser';
import Logo from 'img/nusmods-logo.svg';
import type { State } from 'types/state';
import FeedbackModal from './components/FeedbackModal';
import KeyboardShortcuts from './components/KeyboardShortcuts';

import styles from './AppShell.scss';
import useColorScheme from './hooks/useColorScheme';

const AppShell: FC<PropsWithChildren> = ({ children }) => {
  // Enable Matomo analytics
  const history = useHistory();
  useEffect(() => trackPageView(history), [history]);

  const colorScheme = useColorScheme();
  const isDarkMode = colorScheme === DARK_COLOR_SCHEME;

  const theme = useSelector((state: State) => state.theme.id);

  return (
    <div className="app-container">
      <Helmet>
        <body
          className={classnames(`theme-${theme}`, {
            'mode-dark': isDarkMode,
            'mdc-theme--dark': isDarkMode,
            'mobile-safari': isIOS,
          })}
        />
      </Helmet>

      <nav className={styles.navbar}>
        <NavLink className={styles.brand} to="/" title="Home">
          <Logo className={styles.brandLogo} title="NUSMods" />
        </NavLink>

        <div className={styles.navRight}>
          <div className={styles.weekText}>{weekText}</div>
        </div>
      </nav>

      <div className="main-container">
        <Navtabs />

        <main className="main-content">
          <ErrorBoundary errorPage={() => <ErrorPage showReportDialog />}>{children}</ErrorBoundary>
        </main>
      </div>

      <ErrorBoundary>
        <FeedbackModal />
      </ErrorBoundary>

      <ErrorBoundary>
        <Notification />
      </ErrorBoundary>

      <ErrorBoundary>
        <Footer />
      </ErrorBoundary>

      <ErrorBoundary>
        <KeyboardShortcuts />
      </ErrorBoundary>
    </div>
  );
};

export default AppShell;
